import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Seo from '../components/Seo';
import { getArticleBySlug, getArticleCopy } from '../data/articles';
import { loadArticle } from '../utils/markdownLoader';
import { getArticleMeta } from '../seo/pageMeta';

const ArticleScreen = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const article = getArticleBySlug(slug);
  const copy = article ? getArticleCopy(article, i18n.language) : null;
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(Boolean(article));
  const [failed, setFailed] = useState(!article);

  useEffect(() => {
    let cancelled = false;

    if (!article) {
      setHtml(null);
      setLoading(false);
      setFailed(true);
      return undefined;
    }

    setLoading(true);
    setFailed(false);

    loadArticle(article.slug, i18n.language).then((content) => {
      if (cancelled) return;
      setHtml(content);
      setLoading(false);
      setFailed(!content);
    });

    return () => {
      cancelled = true;
    };
  }, [article, i18n.language]);

  const meta = copy
    ? getArticleMeta(copy.title, copy.description, i18n.language)
    : {
        title: t('resources.tradingEducation.notFoundTitle'),
        description: t('resources.tradingEducation.notFound'),
      };

  return (
    <div className="min-h-screen bg-bg text-body pt-28 pb-24 md:pt-32">
      <Seo
        title={meta.title}
        description={meta.description}
        path={article ? `/resources/${article.slug}` : '/resources'}
        ready={!loading}
      />
      <div className="mx-auto max-w-page px-6">
        <Link
          to="/resources"
          className="mb-10 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-body"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {t('resources.tradingEducation.backToResources')}
        </Link>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        )}

        {!loading && failed && (
          <Message variant="info">{t('resources.tradingEducation.notFound')}</Message>
        )}

        {!loading && html && (
          <article
            className="product-detail-markdown prose prose-invert prose-lg mx-auto max-w-3xl text-muted prose-headings:font-semibold prose-headings:text-body prose-a:text-primary prose-strong:text-body prose-p:leading-relaxed prose-li:marker:text-muted"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </div>
    </div>
  );
};

export default ArticleScreen;
